// Ported from the pre-upgrade src/main.tsx. Everything here touches window,
// localStorage or console, so it runs once in the browser after hydration and
// never during server rendering.
import { reportWebVitals } from "@/lib/webVitals";
import { initSentry } from "@/lib/sentry";

let started = false;

export function runClientBootstrap() {
  if (started || typeof window === "undefined") return;
  started = true;

  // Filter known-benign Cloudflare Turnstile console noise. The widget posts to
  // its challenges.cloudflare.com iframe before the iframe has finished
  // navigating to that origin, producing a postMessage target-origin warning
  // that retries successfully. Also suppresses the informational Private Access
  // Token challenge log. Real errors still surface.
  (() => {
    const TURNSTILE_NOISE = [
      "target origin provided ('https://challenges.cloudflare.com')",
      "Request for the Private Access Token challenge",
    ];
    const isNoise = (args: unknown[]) => {
      const first = args[0];
      if (typeof first !== "string") return false;
      return TURNSTILE_NOISE.some((s) => first.includes(s));
    };
    const origError = console.error.bind(console);
    const origLog = console.log.bind(console);
    console.error = (...args: unknown[]) => {
      if (!isNoise(args)) origError(...args);
    };
    console.log = (...args: unknown[]) => {
      if (!isNoise(args)) origLog(...args);
    };
  })();

  // Sentry error monitoring — gated on user consent. Strictly necessary
  // auth/Supabase loads regardless; Sentry only initializes when the visitor
  // has accepted analytics cookies.
  try {
    if (localStorage.getItem("cookie-consent") === "accepted") {
      initSentry();
    }
    window.addEventListener("consent-changed", (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === "accepted") initSentry();
    });
  } catch {
    /* localStorage unavailable — skip analytics init */
  }

  // Stale-chunk recovery: after a deploy, an open tab can request a lazy-loaded
  // page file whose hashed name no longer exists. Reload once to pick up the new
  // version instead of showing the error screen. The sessionStorage flag stops a
  // reload loop if the new version is itself broken.
  (() => {
    const RELOAD_FLAG = "chunk-reload-attempted";
    const isChunkError = (msg: unknown) =>
      typeof msg === "string" &&
      (msg.includes("dynamically imported module") || msg.includes("Importing a module script failed"));
    const tryReload = () => {
      try {
        if (sessionStorage.getItem(RELOAD_FLAG)) return;
        sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch {
        /* storage unavailable — still reload once */
      }
      window.location.reload();
    };
    window.addEventListener("vite:preloadError", () => tryReload());
    window.addEventListener("unhandledrejection", (e) => {
      if (isChunkError(e.reason?.message)) tryReload();
    });
    window.addEventListener("error", (e) => {
      if (isChunkError(e.message)) tryReload();
    });
    // The page has hydrated: clear the flag so a later deploy can trigger one reload again.
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignore */
    }
  })();

  // Report Core Web Vitals
  reportWebVitals();
}
